﻿import React, { ChangeEvent, useState } from 'react';
import { IOrderType } from '../../../../Entities';
import {
    BrokerAgent,
    BrokerAgentFilterOptions,
    FreeTextFilterOptions,
    IOrderCollection,
    OrderCollectionsWithDate,
    OrderPlacedAtFilterOptions,
    OrderTypeFilterOptions
} from '../types';
import FilterOptions from './FilterOptions';
import OrderCollectionSection from './OrderCollectionSection';
import OrderListSection from './OrderListSection';
import {
    filterOrdersOnBrokerAgent,
    filterOrdersOnOrderType,
    transformOrderCollectionToOrders
} from '../utils/filterOrdersUtils';
import {
    filterOrderCollectionOnOrderPlacedAtDate,
    filterOrdersOnFreeTextSearch,
} from '../../v1/utils/filterOrdersUtils';
import {
    sortOrderCollectionsByLastOrderPlacedAtDesc,
    sortOrdersByDateDesc
} from '../utils/orderDateUtils';
import NoSearchResults from './NoSearchResults';
import { formatDate } from '../../../../../../shared/LanguageService';

export type OrderListContainerProps = {
    availableBrokerAgents: BrokerAgent[];
    availableOrderTypes: IOrderType[];
    orderCollectionsWithDate: OrderCollectionsWithDate[];
    earliestAvailableOrderDate: Date;
    user: any;
    
    selectedOrderType: number | null;
    searchPhrase: string;
    selectedBrokerAgent: number | null;
    showArchived: boolean;
    orderPlacedAtStart: Date;
    orderPlacedAtEnd: Date;

    setSelectedOrderType(id: number | null): void;
    setSearchPhrase(search: string): void;
    setSelectedBrokerAgent(id: number | null): void;
    setShowArchived(value: boolean): void;
    setOrderPlacedAtStart(date: Date): void;
    setOrderPlacedAtEnd(date: Date): void;
};

const MONTH_HEADING_DATE_FORMAT = 'LLLL yyyy';

function OrderListContainer({
    availableBrokerAgents,
    availableOrderTypes,
    orderCollectionsWithDate,
    earliestAvailableOrderDate,
    user,

    selectedOrderType,
    selectedBrokerAgent,
    searchPhrase,
    orderPlacedAtStart,
    orderPlacedAtEnd,

    setSelectedOrderType,
    setSearchPhrase,
    setSelectedBrokerAgent,
    setOrderPlacedAtStart,
    setOrderPlacedAtEnd
}: OrderListContainerProps) {
    const [expandedSections, setExpandedSections] = useState<number[]>([0, 1, 2]);

    const orderTypeFilterOptions: OrderTypeFilterOptions = {
        availableOrderTypes,
        orderType: selectedOrderType,
        onChangeOrderType: (event: ChangeEvent<HTMLSelectElement>) => {
            const value = Number(event.target.value);
            if (value === -1) {
                setSelectedOrderType(null);
            } else {
                setSelectedOrderType(Number(event.target.value));
            }
        }
    };

    const brokerAgentFilterOptions: BrokerAgentFilterOptions = {
        availableBrokerAgents,
        brokerAgent: selectedBrokerAgent,
        onChangeBrokerAgent: (event: ChangeEvent<HTMLSelectElement>) => {
            const value = Number(event.target.value);
            if (value === -1) {
                setSelectedBrokerAgent(null);
            } else {
                setSelectedBrokerAgent(value);
            }
        }
    };

    const freeTextFilterOptions: FreeTextFilterOptions = {
        searchPhrase,
        onChangeFreeText: (event: ChangeEvent<HTMLInputElement>) => {
            setSearchPhrase(event.target.value.toLowerCase());
        }
    };

    const orderPlacedAtFilterOptions: OrderPlacedAtFilterOptions = {
        earliestAvailableOrderDate,
        orderPlacedAtStart,
        orderPlacedAtEnd,
        onOrderPlacedAtStartChange: (newDate: Date) => {
            setOrderPlacedAtStart(newDate);
        },
        onOrderPlacedAtEndChange: (newDate: Date) => {
            setOrderPlacedAtEnd(newDate);
        }
    };

    const shouldShowAllOrderTypes: boolean = selectedOrderType === null;

    function filterOrderCollectionsOnOrderType(orderCollection: IOrderCollection): boolean {
        if (selectedOrderType === null) {
            return true;
        }

        return orderCollection.orders.some(filterOrdersOnOrderType(selectedOrderType));
    }

    function filterOrderCollectionsOnBrokerAgent(orderCollection: IOrderCollection): boolean {
        if (selectedBrokerAgent === null) {
            return true;
        }

        return orderCollection.orders.some(filterOrdersOnBrokerAgent(selectedBrokerAgent));
    }

    function filterOrderCollectionsOnFreeTextSearch(orderCollection: IOrderCollection): boolean {
        if (searchPhrase === '') {
            return true;
        }

        let hasMatches = false;

        const orderNumbers = orderCollection.orderNumberRange.split('-');
        const fullOrderNumbers = orderNumbers.map(
            (orderNumber) => `${orderCollection.orderCollectionNumber}:${orderNumber}`
        );
        if (
            `${orderCollection.orderCollectionNumber}`.includes(searchPhrase) ||
            fullOrderNumbers.some((fullOrderNumber) => fullOrderNumber.includes(searchPhrase))
        ) {
            hasMatches = true;
        }

        const name = orderCollection.name ? orderCollection.name.toLowerCase() : '';
        if (name.includes(searchPhrase)) {
            hasMatches = true;
        }

        const city = orderCollection.city ? orderCollection.city.toLowerCase() : '';
        if (city.includes(searchPhrase)) {
            hasMatches = true;
        }

        hasMatches =
            hasMatches || orderCollection.orders.some(filterOrdersOnFreeTextSearch(searchPhrase));

        return hasMatches;
    }

    function updateExpandedSectionsList(idx) {
        setExpandedSections((prevExpandedSections) => {
            if (prevExpandedSections.includes(idx)) {
                return prevExpandedSections.filter(index => index != idx);
            }
        
            const updatedSections = [...prevExpandedSections, idx];
        
            if (updatedSections.length > 5) {
                const farthestIndex = updatedSections.reduce((farthestIdx, section, currentIndex) => {
                    const distance = Math.abs(idx - section);
                    if (distance > Math.abs(idx - updatedSections[farthestIdx])) {
                        return currentIndex;
                    }
                    return farthestIdx;
                }, 0);
        
                updatedSections.splice(farthestIndex, 1);
            }
    
            return updatedSections;
        });
      }

    const filteredOrderCollectionsWithDate = orderCollectionsWithDate.filter(
        ({ orderCollections }) => {
            const res = orderCollections
                .filter(filterOrderCollectionsOnOrderType)
                .filter(filterOrderCollectionsOnBrokerAgent)
                .filter(filterOrderCollectionsOnFreeTextSearch)
                .filter(
                    filterOrderCollectionOnOrderPlacedAtDate(
                        orderPlacedAtStart,
                        orderPlacedAtEnd,
                        earliestAvailableOrderDate
                    )
                );
            return res.length > 0;
        }
    );

    let filteredOrderCollections = null;
    let filteredOrders = null;

    if (shouldShowAllOrderTypes) {
        filteredOrderCollections = filteredOrderCollectionsWithDate.map(
            ({ title, orderCollections }, idx) => {
              const filteredCollections = orderCollections
                .filter(filterOrderCollectionsOnBrokerAgent)
                .filter(filterOrderCollectionsOnFreeTextSearch)
                .map((collection) => {
                  const orders = collection.orders
                    .filter(filterOrdersOnBrokerAgent(selectedBrokerAgent))
                    .filter(filterOrdersOnFreeTextSearch(searchPhrase))
                  return {
                    ...collection,
                    orders
                  };
                })
                .sort(sortOrderCollectionsByLastOrderPlacedAtDesc);
          
              return {
                idx,
                title: formatDate(new Date(title), MONTH_HEADING_DATE_FORMAT),
                collections: filteredCollections
              };
            }
        );
    } else {
        filteredOrders = filteredOrderCollectionsWithDate
            .map(transformOrderCollectionToOrders)
            .map(({ title, orders }, idx) => {
                return {
                    idx,
                    title: formatDate(new Date(title), MONTH_HEADING_DATE_FORMAT),
                    orders: orders
                        .filter(filterOrdersOnOrderType(selectedOrderType))
                        .filter(filterOrdersOnBrokerAgent(selectedBrokerAgent))
                        .filter(filterOrdersOnFreeTextSearch(searchPhrase))
                        .sort(sortOrdersByDateDesc)
                };
            });
    }

    return (
        <div>
            <FilterOptions
                orderTypeFilterOptions={orderTypeFilterOptions}
                brokerAgentFilterOptions={brokerAgentFilterOptions}
                freeTextFilterOptions={freeTextFilterOptions}
                orderPlacedAtFilterOptions={orderPlacedAtFilterOptions}
            />
            <div style={{ paddingTop: "16px" }}>
                {shouldShowAllOrderTypes ? (
                    filteredOrderCollections.length > 0 ? (
                        filteredOrderCollections.map(({ idx, title, collections }) => (
                            <OrderCollectionSection
                                key={title}
                                title={title}
                                isOpen={expandedSections.includes(idx)}
                                setIsOpen={() => updateExpandedSectionsList(idx)}
                                orderCollections={collections}
                                user={user}
                                navigateTo={() => {
                                    /* TODO */
                                }}
                                newOrderId={-1}
                            />
                        ))
                    ) : (
                        <NoSearchResults />
                    )
                ) : filteredOrders.length > 0 ? (
                    filteredOrders.map(({ idx, title, orders }) => (
                        <OrderListSection
                            key={title}
                            title={title}
                            isOpen={expandedSections.includes(idx)}
                            setIsOpen={() => updateExpandedSectionsList(idx)}
                            orders={orders}
                            user={user}
                            navigateTo={() => {
                                /* TODO */
                            }}
                            newOrderId={-1}
                        />
                    ))
                ) : (
                    <NoSearchResults />
                )}
            </div>
        </div>
    );
}

export default OrderListContainer;